import { useState, useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import toast from 'react-hot-toast';
import { familyService } from '@/api/familyService';
import { logger } from '@/utils/logger';

interface PassedAdultData {
  id: string;
  firstName: string;
  lastName: string;
  birthMonth: number;
  birthYear: number;
  phone: string;
  email?: string;
  gender: string;
  relationshipToChild: string;
}

interface PassedChildData {
  id: string;
  firstName: string;
  lastName: string;
  birthMonth: number;
  birthYear: number;
  gender: string;
}

interface RegistrationData {
  adults: PassedAdultData[];
  children: PassedChildData[];
  pregnancy: {
    isPregnant: boolean;
    expectedDeliveryMonth: number | null;
    expectedDeliveryYear: number | null;
    expectedBabies: number | null;
  };
  homePostalCode: string;
  isLowIncome: boolean | null;
  languagesSpoken: string[];
  otherLanguage: string | null;
  hearAboutUs: string[];
  hearAboutUsOther: string | null;
  referralDetails: string | null;
  consentGiven: boolean;
  consentDate: string;
}

interface ConsentRecord {
  id: string;
  name: string;
  type: 'adult' | 'child';
  isGiven: boolean;
  isNameGiven: boolean;
}

interface ConsentToggleProps {
  id: string;
  groupName: string;
  label: string;
  value: boolean; // can be null/undefined initially, but typings in state are boolean
  onChange: (id: string, value: boolean) => void;
  labels: {
    yes: string;
    no: string;
  };
}

function ConsentToggle({ id, groupName, label, value, onChange, labels }: ConsentToggleProps) {
  return (
    <div className="space-y-2">
      <Label className="text-sm md:text-base font-semibold text-gray-700 block">{label}</Label>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <label
          htmlFor={`${groupName}-yes-${id}`}
          className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all min-h-[56px] ${
            value === true
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50/50'
          }`}
        >
          <input
            type="radio"
            id={`${groupName}-yes-${id}`}
            name={`${groupName}-${id}`}
            checked={value === true}
            onChange={() => onChange(id, true)}
            className="sr-only"
          />
          <div
            className={`flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center ${
              value === true ? 'bg-blue-600 border-blue-600' : 'border-gray-300 bg-white'
            }`}
          >
            {value === true && <div className="w-2.5 h-2.5 rounded-full bg-white"></div>}
          </div>
          <span className="text-sm font-medium text-gray-900 leading-tight">{labels.yes}</span>
        </label>

        <label
          htmlFor={`${groupName}-no-${id}`}
          className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all min-h-[56px] ${
            value === false
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50/50'
          }`}
        >
          <input
            type="radio"
            id={`${groupName}-no-${id}`}
            name={`${groupName}-${id}`}
            checked={value === false}
            onChange={() => onChange(id, false)}
            className="sr-only"
          />
          <div
            className={`flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center ${
              value === false ? 'bg-blue-600 border-blue-600' : 'border-gray-300 bg-white'
            }`}
          >
            {value === false && <div className="w-2.5 h-2.5 rounded-full bg-white"></div>}
          </div>
          <span className="text-sm font-medium text-gray-900 leading-tight">{labels.no}</span>
        </label>
      </div>
    </div>
  );
}

export function PhotoReleasePage() {
  const { t } = useTranslation('registration');
  const navigate = useNavigate();
  const location = useLocation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const registrationData = location.state?.registrationData as RegistrationData | undefined;

  const [consents, setConsents] = useState<ConsentRecord[]>([]);

  useEffect(() => {
    if (registrationData) {
      const initialConsents: ConsentRecord[] = [];

      registrationData.adults.forEach((adult) => {
        initialConsents.push({
          id: adult.id,
          name: `${adult.firstName} ${adult.lastName}`,
          type: 'adult',
          isGiven: false,
          isNameGiven: false,
        });
      });

      registrationData.children.forEach((child) => {
        initialConsents.push({
          id: child.id,
          name: `${child.firstName} ${child.lastName}`,
          type: 'child',
          isGiven: false,
          isNameGiven: false,
        });
      });

      setConsents(initialConsents);
    } else {
      toast.error(t('photoRelease.toasts.redirect'));
      navigate('/register');
    }
  }, [registrationData, navigate]);

  const handleConsentChange = (id: string, isGiven: boolean) => {
    setConsents((prev) => prev.map((c) => (c.id === id ? { ...c, isGiven } : c)));
  };

  const handleNameConsentChange = (id: string, isNameGiven: boolean) => {
    setConsents((prev) => prev.map((c) => (c.id === id ? { ...c, isNameGiven } : c)));
  };

  const handleBackClick = () => {
    if (window.confirm(t('photoRelease.toasts.unsaved'))) {
      navigate('/register');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const missingMediaConsent = consents.filter((c) => c.isGiven === null);
    const missingNameConsent = consents.filter((c) => c.isNameGiven === null);

    if (missingMediaConsent.length > 0) {
      toast.error(t('photoRelease.toasts.selectMedia', { names: missingMediaConsent.map((c) => c.name).join(', ') }));
      return;
    }

    if (missingNameConsent.length > 0) {
      toast.error(t('photoRelease.toasts.selectName', { names: missingNameConsent.map((c) => c.name).join(', ') }));
      return;
    }

    if (!registrationData) {
      toast.error(t('photoRelease.toasts.missingData'));
      return;
    }

    setIsSubmitting(true);

    try {
      const finalPayload = {
        ...registrationData,
        photoReleaseConsents: consents,
        consentDate: new Date().toISOString(),
      };

      logger.log('Submitting final registration payload:', finalPayload);

      const response = await familyService.registerFamily(finalPayload);
      logger.log('Registration success:', response);

      localStorage.removeItem('frc_registration_draft');

      toast.success(t('photoRelease.toasts.success'));

      setTimeout(() => {
        navigate('/my-families');
      }, 1500);
    } catch (error) {
      logger.error('Error submitting form:', error);
      toast.error(t('photoRelease.toasts.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!registrationData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('photoRelease.headers.noData')}</h2>
          <Button onClick={() => navigate('/register')}>{t('photoRelease.actions.goToRegistration')}</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-yellow-50 via-pink-50 to-blue-50 px-3 py-4 sm:px-4 sm:py-6 md:px-6 lg:px-8 landscape:py-3 landscape:sm:py-4">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-lg md:rounded-xl shadow-xl p-4 sm:p-6 md:p-8 landscape:p-4">
          {/* Header */}
          <div className="relative mb-6">
            <div className="absolute left-0 top-0">
              <Button
                type="button"
                onClick={handleBackClick}
                className="touch-feedback bg-purple-600 hover:bg-purple-700 text-white px-3 py-2.5 sm:px-4 sm:py-2.5 text-sm min-h-[44px] sm:min-h-[40px] font-medium rounded shadow-sm transition-colors duration-200"
              >
                ← {t('photoRelease.buttons.back')}
              </Button>
            </div>

            <div className="text-center pt-8 sm:pt-0">
              <div className="mb-3 landscape:mb-1">
                <img
                  src="/logo.png"
                  alt="Greater Moncton Homelessness and Housing Steering Committee"
                  className="mx-auto w-24 landscape:w-16 h-auto"
                />
              </div>
              <h1 className="text-xl sm:text-2xl md:text-3xl landscape:text-base font-bold text-gray-900 mb-2 landscape:mb-1">
                {t('photoRelease.title')}
              </h1>
              <p className="text-sm md:text-base landscape:text-sm text-gray-600 max-w-lg mx-auto">
                {t('photoRelease.description')}
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8 landscape:space-y-4">
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-5 landscape:p-3 text-sm md:text-base landscape:text-sm text-blue-900 leading-relaxed shadow-sm">
              <h3 className="font-semibold text-blue-800 mb-3 text-base">{t('photoRelease.agreement.title')}</h3>
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-blue-900 mb-1">{t('photoRelease.agreement.media.title')}</h4>
                  <p className="mb-2">
                    <Trans i18nKey="photoRelease.agreement.media.content" t={t}>
                      I hereby consent that any photographs or video recordings taken of me and/or my child(ren) by the
                      <strong> Greater Moncton Homelessness and Housing Steering Committee (GMHSC)</strong>
                      on this date and subsequent dates, may be used to promote or report on the GMHSC. This includes
                      usage for publicity, illustration, advertising, and web content.
                    </Trans>
                  </p>
                  <p className="mb-1 text-blue-900 font-medium">{t('photoRelease.agreement.media.purposesHeader')}</p>
                  <ul className="list-disc ml-5 space-y-1 text-left">
                    {(t('photoRelease.agreement.media.purposes', { returnObjects: true }) as string[]).map(
                      (purpose, index) => (
                        <li key={index}>{purpose}</li>
                      ),
                    )}
                  </ul>
                  <p className="mt-3 text-sm text-blue-800 italic border-t border-blue-200 pt-2">
                    <Trans i18nKey="photoRelease.agreement.media.note" t={t}>
                      <span className="font-bold">NOTE:</span> A person whose image appears in a photograph taken during
                      this event may request access to the photograph during the time it is held by GMHSC, and GMHSC
                      will make every reasonable effort to provide access to the requested photograph.
                    </Trans>
                  </p>
                </div>
                <div>
                  <h4 className="font-medium text-blue-900 mb-1">{t('photoRelease.agreement.name.title')}</h4>
                  <p>{t('photoRelease.agreement.name.content')}</p>
                  <p className="mt-2 text-sm text-blue-800 italic border-t border-blue-200 pt-2">
                    <Trans i18nKey="photoRelease.agreement.name.note" t={t}>
                      <span className="font-bold">NOTE:</span> For children under 19, only the child's first name and
                      age will be used as identification.
                    </Trans>
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              {consents.map((person) => (
                <div
                  key={person.id}
                  className="border border-gray-200 rounded-lg p-5 bg-white shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4 border-b border-gray-100 pb-4">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">{person.name}</h3>
                      <span
                        className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium ${person.type === 'adult' ? 'bg-green-100 text-green-800' : 'bg-purple-100 text-purple-800'}`}
                      >
                        {person.type === 'adult' ? t('photoRelease.labels.adult') : t('photoRelease.labels.child')}
                      </span>
                    </div>
                  </div>

                  {/* Permissions Section */}
                  <div className="space-y-4 mb-6">
                    {/* Photo Permission */}
                    <ConsentToggle
                      id={person.id}
                      groupName="media"
                      label={t('photoRelease.labels.mediaConsent')}
                      value={person.isGiven}
                      onChange={handleConsentChange}
                      labels={{
                        yes: t('binaryOptions.yes'),
                        no: t('binaryOptions.no'),
                      }}
                    />

                    {/* Name Permission */}
                    <ConsentToggle
                      id={person.id}
                      groupName="name"
                      label={t('photoRelease.labels.nameConsent')}
                      value={person.isNameGiven}
                      onChange={handleNameConsentChange}
                      labels={{
                        yes: t('binaryOptions.yes'),
                        no: t('binaryOptions.no'),
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="pt-4 landscape:pt-2 border-t border-gray-100">
              <Button
                type="submit"
                disabled={isSubmitting}
                className="touch-feedback w-full bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:-translate-y-0.5"
              >
                {isSubmitting ? t('photoRelease.buttons.finishing') : t('photoRelease.buttons.complete')}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
